import { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AlertTriangleIcon, InfoIcon, RefreshCwIcon } from "lucide-react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { num } from "@/lib/matching";
import { formatNumber } from "@/lib/format";
import { chartTooltipProps } from "@/components/charts/ChartTooltip";

type DocumentVolumeDailyRow = components["schemas"]["DocumentVolumeDailyRow"];

/** Section title InfoHint copy, wired up in AnalyticsPage.tsx. */
export const DOCUMENT_VOLUME_INFO_HINT =
  "A bill that arrived in a company inbox counts on the day it was given an organisation, not the day it arrived: assigning it overwrites the arrival time. The Inbox routing section counts those bills by the day they arrived.";

function buildQuery(apiParams: URLSearchParams) {
  return {
    From: apiParams.get("From") ?? "",
    To: apiParams.get("To") ?? "",
    ClientIds: apiParams.getAll("ClientIds"),
    OrganisationId: apiParams.get("OrganisationId") || undefined,
  };
}

/** "2026-09-08" -> "8 Sep". Parsed by hand so a date-only string never shifts with the local timezone. */
function dateLabel(dateStr: string): string {
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return dateStr;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

interface SeriesSpec {
  key: "bills" | "purchaseOrders" | "deliveryNotes";
  label: string;
  /** A `var(--...)` into VIZ_STYLES below, never a literal hex. */
  color: string;
}

interface DailyPoint {
  date: string;
  label: string;
  bills: number;
  purchaseOrders: number;
  deliveryNotes: number;
  total: number;
}

interface OrgTotals {
  organisationId: string;
  organisationName: string;
  bills: number;
  purchaseOrders: number;
  deliveryNotes: number;
  total: number;
}

const VIZ_CLASS = "dolfin-document-volume-viz";

/** Palette slots 1-3, as in matchOutcomeBuckets.ts (adjacent pairs validated in both modes); scoped here, not imported. */
const VIZ_STYLES = `
.${VIZ_CLASS} {
  --viz-surface: #fcfcfb;
  --series-1: #2a78d6;
  --series-2: #eb6834;
  --series-3: #1baf7a;
}
.dark .${VIZ_CLASS} {
  --viz-surface: #1a1a19;
  --series-1: #3987e5;
  --series-2: #d95926;
  --series-3: #199e70;
}
`;


const DOCUMENT_TYPE_SERIES: SeriesSpec[] = [
  { key: "bills", label: "Bills", color: "var(--series-1)" },
  { key: "purchaseOrders", label: "Purchase orders", color: "var(--series-2)" },
  { key: "deliveryNotes", label: "Delivery notes", color: "var(--series-3)" },
];

function plural(count: number, singular: string, pluralForm: string): string {
  return count === 1 ? singular : pluralForm;
}

export interface VolumeTotals {
  bills: number;
  purchaseOrders: number;
  deliveryNotes: number;
  total: number;
}

/** The headline sentence. Zeros are printed: "0 delivery notes" is an answer. */
export function volumeHeadline(totals: VolumeTotals): string {
  if (totals.total === 0) return "No documents in this window.";
  const parts = [
    `${formatNumber(totals.bills)} ${plural(totals.bills, "bill", "bills")}`,
    `${formatNumber(totals.purchaseOrders)} ${plural(totals.purchaseOrders, "purchase order", "purchase orders")}`,
    `${formatNumber(totals.deliveryNotes)} ${plural(totals.deliveryNotes, "delivery note", "delivery notes")}`,
  ];
  return `Received ${formatNumber(totals.total)} ${plural(totals.total, "document", "documents")}: ${parts.join(", ")}.`;
}

export interface DocumentVolumeChartProps {
  apiParams: URLSearchParams;
}

/** Section 3: documents arriving per day, stacked by type; the per-organisation view is a table in Data details. */
export function DocumentVolumeChart({ apiParams }: DocumentVolumeChartProps) {
  const apiParamsKey = apiParams.toString();
  const [rows, setRows] = useState<DocumentVolumeDailyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/analytics/document-volume-daily", {
        params: { query: buildQuery(apiParams) },
      });
      if (cancelled) return;
      if (apiError || !data) {
        setError("Document volume could not be loaded.");
      } else {
        setRows(data);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParamsKey, refreshKey]);

  // The backend returns a row per organisation per day; collapse to one point per day.
  const points = useMemo<DailyPoint[]>(() => {
    const byDate = new Map<string, DailyPoint>();
    for (const row of rows) {
      const bills = num(row.bills);
      const purchaseOrders = num(row.purchaseOrders);
      const deliveryNotes = num(row.deliveryNotes);
      const existing = byDate.get(row.date);
      if (existing) {
        existing.bills += bills;
        existing.purchaseOrders += purchaseOrders;
        existing.deliveryNotes += deliveryNotes;
        existing.total += bills + purchaseOrders + deliveryNotes;
      } else {
        byDate.set(row.date, {
          date: row.date,
          label: dateLabel(row.date),
          bills,
          purchaseOrders,
          deliveryNotes,
          total: bills + purchaseOrders + deliveryNotes,
        });
      }
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);

  const totals = useMemo<VolumeTotals>(
    () =>
      points.reduce<VolumeTotals>(
        (acc, point) => ({
          bills: acc.bills + point.bills,
          purchaseOrders: acc.purchaseOrders + point.purchaseOrders,
          deliveryNotes: acc.deliveryNotes + point.deliveryNotes,
          total: acc.total + point.total,
        }),
        { bills: 0, purchaseOrders: 0, deliveryNotes: 0, total: 0 },
      ),
    [points],
  );

  /** Per-organisation totals for Data details. */
  const orgTotals = useMemo<OrgTotals[]>(() => {
    const map = new Map<string, OrgTotals>();
    for (const row of rows) {
      let entry = map.get(row.organisationId);
      if (!entry) {
        entry = {
          organisationId: row.organisationId,
          organisationName: row.organisationName,
          bills: 0,
          purchaseOrders: 0,
          deliveryNotes: 0,
          total: 0,
        };
        map.set(row.organisationId, entry);
      }
      const bills = num(row.bills);
      const purchaseOrders = num(row.purchaseOrders);
      const deliveryNotes = num(row.deliveryNotes);
      entry.bills += bills;
      entry.purchaseOrders += purchaseOrders;
      entry.deliveryNotes += deliveryNotes;
      entry.total += bills + purchaseOrders + deliveryNotes;
    }
    return Array.from(map.values()).sort(
      (a, b) => b.total - a.total || a.organisationName.localeCompare(b.organisationName),
    );
  }, [rows]);

  const hasActivity = totals.total > 0;

  if (loading && rows.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <div className="bg-muted h-6 w-2/3 animate-pulse rounded" />
        <div className="bg-muted h-64 animate-pulse rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-destructive/30 bg-destructive/5 flex flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
        <AlertTriangleIcon className="text-destructive size-6" />
        <p className="text-sm font-medium">Could not load document volume</p>
        <p className="text-muted-foreground max-w-md text-sm">{error}</p>
        <button
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          className="border-border hover:bg-accent mt-1 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition"
        >
          <RefreshCwIcon className="size-3.5" />
          Try again
        </button>
      </div>
    );
  }

  if (rows.length === 0 || !hasActivity) {
    return (
      <div className="border-border flex flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center">
        <InfoIcon className="text-muted-foreground size-6" />
        <p className="text-sm font-medium">No documents in this window</p>
        <p className="text-muted-foreground max-w-md text-sm">
          Widen the date range, or check that the selected client has document activity.
        </p>
      </div>
    );
  }

  return (
    <div className={`${VIZ_CLASS} flex flex-col gap-6`}>
      <style>{VIZ_STYLES}</style>

      <div>
        <p className="text-lg leading-snug font-medium">{volumeHeadline(totals)}</p>
        <p className="text-muted-foreground mt-1 text-sm">
          Bills, purchase orders and delivery notes, counted on the day they arrived.
        </p>
      </div>

      <VolumePanel points={points} />

      <Accordion type="single" collapsible className="border-t">
        <AccordionItem value="details" className="border-b-0">
          <AccordionTrigger>Data details</AccordionTrigger>
          <AccordionContent className="space-y-6 text-sm">
            <div>
              <p className="font-medium">Organisation by organisation</p>
              <p className="text-muted-foreground mt-1 text-xs">
                Every organisation with documents in this range, largest first.
              </p>
              <div className="mt-2 max-h-96 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organisation</TableHead>
                      <TableHead className="text-right">Bills</TableHead>
                      <TableHead className="text-right">Purchase orders</TableHead>
                      <TableHead className="text-right">Delivery notes</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orgTotals.map((org) => (
                      <TableRow key={org.organisationId}>
                        <TableCell className="max-w-48 truncate" title={org.organisationName}>
                          {org.organisationName}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatNumber(org.bills)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(org.purchaseOrders)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(org.deliveryNotes)}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatNumber(org.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div>
              <p className="font-medium">Day by day</p>
              <p className="text-muted-foreground mt-1 text-xs">
                The same numbers the chart above draws, one row per day.
              </p>
              <div className="mt-2 max-h-96 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      {DOCUMENT_TYPE_SERIES.map((series) => (
                        <TableHead key={series.key} className="text-right">
                          {series.label}
                        </TableHead>
                      ))}
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {points.map((point) => (
                      <TableRow key={point.date}>
                        <TableCell className="whitespace-nowrap">{point.label}</TableCell>
                        {DOCUMENT_TYPE_SERIES.map((series) => (
                          <TableCell key={series.key} className="text-right tabular-nums">
                            {formatNumber(point[series.key])}
                          </TableCell>
                        ))}
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatNumber(point.total)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            <p className="text-muted-foreground text-xs">
              A day with no documents has no column in the chart above and no row in the table.
            </p>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function VolumePanel({ points }: { points: DailyPoint[] }) {
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={points} margin={{ top: 4, right: 4, bottom: 0, left: -20 }} barCategoryGap="12%">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-border, #e5e7eb)" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            stroke="var(--color-muted-foreground, #6b7280)"
            interval="preserveStartEnd"
            minTickGap={24}
          />
          <YAxis tick={{ fontSize: 11 }} stroke="var(--color-muted-foreground, #6b7280)" allowDecimals={false} />
          <Tooltip {...chartTooltipProps} />
          {/* Every document type keeps its legend entry at zero or not, so a reader learns the three exist. */}
          <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
          {DOCUMENT_TYPE_SERIES.map((series) => (
            <Bar
              key={series.key}
              dataKey={series.key}
              name={series.label}
              stackId="day"
              fill={series.color}
              stroke="var(--viz-surface)"
              strokeWidth={2}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
